import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useMobileDetection } from '../hooks/useMobileDetection';
import LogoNav from '../components/Navigation/LogoNav';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { config } from '../utils/config';
import { uploadPermitFileToUserBucket } from '../utils/storageUtils';
import './PermitReview.css';

const ACCEPTED_EXTENSIONS = ['.pdf', '.ifc', '.ifczip', '.dwg', '.dxf', '.rvt', '.txt', '.csv'];
const TEXT_EXCERPT_EXTENSIONS = ['.ifc', '.dxf', '.txt', '.csv'];
const MAX_FILES = 8;
const MAX_FILE_BYTES = 15 * 1024 * 1024;
const MAX_EXCERPT_CHARS = 40000;

type BuildingTypology =
  | ''
  | 'part9_house'
  | 'part9_townhouse'
  | 'part9_secondary_suite'
  | 'part9_small_building'
  | 'part3_residential'
  | 'part3_assembly_business'
  | 'part3_care'
  | 'part3_industrial'
  | 'part3'
  | 'other';

type PermitDocumentPayload = {
  name: string;
  contentType: string;
  category: string;
  sizeBytes: number;
  storagePath?: string;
  signedUrl?: string;
  textExcerpt?: string;
};

type SelectedFile = {
  id: string;
  file: File;
  category: string;
};

type FindingStatus =
  | 'meets'
  | 'does_not_meet'
  | 'information_not_available'
  | 'uncertain';

type ComplianceFinding = {
  id: string;
  status: FindingStatus;
  topic: string;
  rationale: string;
  codeReference?: string | null;
  source: 'deterministic' | 'ai' | string;
};

type StatusCounts = {
  meets: number;
  does_not_meet: number;
  information_not_available: number;
  uncertain: number;
};

type RulesetRuleSummary = {
  id: string;
  topic: string;
  codeReference?: string;
  opcode?: string;
};

type RulesetManifest = {
  id: string;
  version: string;
  title: string;
  description?: string;
  executionOrder?: string;
  ruleCount: number;
  formats?: {
    current?: string;
    plannedAdapters?: string[];
  };
  codeFamilies?: string[];
  rules?: RulesetRuleSummary[];
};

type ReviewerAction = 'accept' | 'override' | 'escalate';

type ReviewerDecision = {
  action: ReviewerAction;
  effectiveStatus: FindingStatus;
  note: string;
  reviewedAt: string;
};

type AuditEntry = {
  id: string;
  timestamp: string;
  findingId: string;
  topic: string;
  action: ReviewerAction;
  previousStatus: FindingStatus;
  newStatus: FindingStatus;
  note: string;
  codeReference?: string | null;
};

const ALL_STATUSES: FindingStatus[] = [
  'meets',
  'does_not_meet',
  'information_not_available',
  'uncertain',
];

const countStatuses = (statuses: FindingStatus[]): StatusCounts => {
  const counts: StatusCounts = {
    meets: 0,
    does_not_meet: 0,
    information_not_available: 0,
    uncertain: 0,
  };
  for (const status of statuses) {
    counts[status] += 1;
  }
  return counts;
};

const guessCategory = (filename: string): string => {
  const lower = filename.toLowerCase();
  if (lower.endsWith('.ifc') || lower.endsWith('.ifczip') || lower.endsWith('.rvt')) return 'bim';
  if (lower.endsWith('.dwg') || lower.endsWith('.dxf')) return 'cad';
  if (lower.endsWith('.pdf')) return 'drawing_pdf';
  return 'supporting';
};

const hasAcceptedExtension = (filename: string): boolean => {
  const lower = filename.toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext));
};

const isTextExcerptCandidate = (filename: string): boolean => {
  const lower = filename.toLowerCase();
  return TEXT_EXCERPT_EXTENSIONS.some((ext) => lower.endsWith(ext));
};

const readTextExcerpt = async (file: File): Promise<string | undefined> => {
  if (!isTextExcerptCandidate(file.name)) return undefined;
  try {
    const text = await file.text();
    if (!text.trim()) return undefined;
    return text.length > MAX_EXCERPT_CHARS
      ? `${text.slice(0, MAX_EXCERPT_CHARS)}\n\n[...truncated...]`
      : text;
  } catch {
    return undefined;
  }
};

const PermitReview: React.FC = () => {
  const { isMobile, isTablet } = useMobileDetection();
  const useMobileLayout = isMobile || isTablet;
  const { user } = useAuth();
  const { t, translate, locale } = useLanguage();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const STATUS_LABELS: Record<FindingStatus, string> = {
    meets: t.permit.meets,
    does_not_meet: t.permit.does_not_meet,
    information_not_available: t.permit.information_not_available,
    uncertain: t.permit.uncertain,
  };

  const ACTION_LABELS: Record<ReviewerAction, string> = {
    accept: t.permit.accepted,
    override: t.permit.overridden,
    escalate: t.permit.escalated,
  };

  const [address, setAddress] = useState('');
  const [municipality, setMunicipality] = useState('Toronto');
  const [applicantName, setApplicantName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [buildingTypology, setBuildingTypology] = useState<BuildingTypology>('');
  const [proposedUse, setProposedUse] = useState('');
  const [storeys, setStoreys] = useState('');
  const [grossFloorArea, setGrossFloorArea] = useState('');
  const [notes, setNotes] = useState('');
  const [files, setFiles] = useState<SelectedFile[]>([]);
  const [dragActive, setDragActive] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [reviewText, setReviewText] = useState<string | null>(null);
  const [findings, setFindings] = useState<ComplianceFinding[]>([]);
  const [summary, setSummary] = useState<string | null>(null);
  const [classificationNotes, setClassificationNotes] = useState<string | null>(null);
  const [missingInformation, setMissingInformation] = useState<string[]>([]);
  const [humanReviewSteps, setHumanReviewSteps] = useState<string[]>([]);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [ruleset, setRuleset] = useState<RulesetManifest | null>(null);
  const [rulesetError, setRulesetError] = useState<string | null>(null);
  const [decisions, setDecisions] = useState<Record<string, ReviewerDecision>>({});
  const [auditLog, setAuditLog] = useState<AuditEntry[]>([]);
  const [overrideDrafts, setOverrideDrafts] = useState<
    Record<string, { status: FindingStatus; note: string; open: boolean }>
  >({});
  const [apiCopied, setApiCopied] = useState(false);

  const effectiveFindings = useMemo(
    () =>
      findings.map((f) => ({
        ...f,
        effectiveStatus: decisions[f.id]?.effectiveStatus || f.status,
        decision: decisions[f.id] || null,
      })),
    [findings, decisions]
  );

  const reviewedStatusCounts = useMemo(
    () => countStatuses(effectiveFindings.map((f) => f.effectiveStatus)),
    [effectiveFindings]
  );

  const reviewProgress = useMemo(() => {
    const total = findings.length;
    const reviewed = Object.keys(decisions).length;
    return { total, reviewed, pending: Math.max(total - reviewed, 0) };
  }, [findings.length, decisions]);

  const typologyCardText = useMemo(() => {
    switch (buildingTypology) {
      case 'part9_house':
        return t.permit.typologyCardPart9House;
      case 'part9_townhouse':
        return t.permit.typologyCardPart9Townhouse;
      case 'part9_secondary_suite':
        return t.permit.typologyCardPart9Suite;
      case 'part9_small_building':
        return t.permit.typologyCardPart9Small;
      case 'part3_residential':
        return t.permit.typologyCardPart3Residential;
      case 'part3_assembly_business':
        return t.permit.typologyCardPart3Assembly;
      case 'part3_care':
        return t.permit.typologyCardPart3Care;
      case 'part3_industrial':
        return t.permit.typologyCardPart3Industrial;
      case 'part3':
        return t.permit.typologyCardPart3;
      case 'other':
        return t.permit.typologyCardOther;
      default:
        return t.permit.typologyCardEmpty;
    }
  }, [buildingTypology, t]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${config.API_URL}/api/permit-review/ruleset`, {
          timeout: 15000,
        });
        if (!cancelled && res.data?.success && res.data.ruleset) {
          setRuleset(res.data.ruleset);
          setRulesetError(null);
        }
      } catch (err: any) {
        if (!cancelled) {
          setRulesetError(t.permit.rulesetUnavailable);
          console.warn('Failed to load permit ruleset:', err?.message || err);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const addFiles = useCallback((incoming: FileList | File[]) => {
    const next = Array.from(incoming);
    setError(null);

    setFiles((prev) => {
      const merged = [...prev];
      for (const file of next) {
        if (merged.length >= MAX_FILES) {
          setError(`You can upload up to ${MAX_FILES} files per package.`);
          break;
        }
        if (!hasAcceptedExtension(file.name)) {
          setError(`Unsupported file type: ${file.name}. Use PDF, IFC, DWG, DXF, RVT, TXT, or CSV.`);
          continue;
        }
        if (file.size > MAX_FILE_BYTES) {
          setError(`File too large: ${file.name}. Maximum size is 15MB.`);
          continue;
        }
        if (merged.some((f) => f.file.name === file.name && f.file.size === file.size)) {
          continue;
        }
        merged.push({
          id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 7)}`,
          file,
          category: guessCategory(file.name),
        });
      }
      return merged;
    });
  }, []);

  const removeFile = (id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const onDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(false);
    if (event.dataTransfer.files?.length) {
      addFiles(event.dataTransfer.files);
    }
  };

  const buildSampleFiles = (): SelectedFile[] => {
    const stamp = Date.now();
    const sitePlan = new File(
      ['Sample site plan placeholder for ZoningPal Permit Review grant demo.\n'],
      'site-plan.pdf',
      { type: 'application/pdf' }
    );
    const floorPlan = new File(
      ['Sample floor plan placeholder for Part 9 house demo.\n'],
      'floor-plan.pdf',
      { type: 'application/pdf' }
    );
    const elevation = new File(
      ['Sample elevation placeholder.\n'],
      'south-elevation.pdf',
      { type: 'application/pdf' }
    );
    const ifc = new File(
      [
        `ISO-10303-21;
HEADER;
FILE_DESCRIPTION(('ZoningPal sample Part 9 IFC excerpt'),'2;1');
FILE_NAME('sample-house.ifc','${new Date().toISOString()}',('ZoningPal'),('Grant demo'),'','','');
FILE_SCHEMA(('IFC4'));
ENDSEC;
DATA;
#1=IFCPROJECT('0SampleProject',$,'Sample Part 9 House',$,$,$,$,(#2),$);
#2=IFCBUILDINGSTOREY('0Storey1',$,'Ground Floor',$,$,$,$,$,.ELEMENT.,0.);
#3=IFCSPACE('0Space1',$,'Living Room',$,$,$,$,$,.ELEMENT.,$,$);
#4=IFCDOOR('0Door1',$,'Entry Door',$,$,$,$,$,$,$);
ENDSEC;
END-ISO-10303-21;
`,
      ],
      'sample-house.ifc',
      { type: 'application/x-step' }
    );

    return [
      { id: `sample-site-${stamp}`, file: sitePlan, category: 'drawing_pdf' },
      { id: `sample-floor-${stamp}`, file: floorPlan, category: 'drawing_pdf' },
      { id: `sample-elev-${stamp}`, file: elevation, category: 'drawing_pdf' },
      { id: `sample-ifc-${stamp}`, file: ifc, category: 'bim' },
    ];
  };

  const applySamplePackage = (): {
    application: {
      address: string;
      municipality: string;
      applicantName: string;
      contactEmail: string;
      buildingTypology: BuildingTypology;
      proposedUse: string;
      storeys: string;
      grossFloorArea: string;
      notes: string;
    };
    selectedFiles: SelectedFile[];
  } => {
    const selectedFiles = buildSampleFiles();
    const application = {
      address: '123 Demo Street, Toronto, ON',
      municipality: 'Toronto',
      applicantName: 'Grant Demo Applicant',
      contactEmail: 'demo@zoningpal.com',
      buildingTypology: 'part9_house' as BuildingTypology,
      proposedUse: 'Single detached dwelling with secondary suite',
      storeys: '2',
      grossFloorArea: '240',
      notes:
        'ISC/NRC grant demo package — Part 9 house archetype with sample PDF drawing names and IFC model excerpt.',
    };

    setAddress(application.address);
    setMunicipality(application.municipality);
    setApplicantName(application.applicantName);
    setContactEmail(application.contactEmail);
    setBuildingTypology(application.buildingTypology);
    setProposedUse(application.proposedUse);
    setStoreys(application.storeys);
    setGrossFloorArea(application.grossFloorArea);
    setNotes(application.notes);
    setFiles(selectedFiles);
    setError(null);
    setInfo(t.permit.sampleLoaded);

    return { application, selectedFiles };
  };

  const runReview = async (
    applicationInput: {
      address: string;
      municipality: string;
      applicantName: string;
      contactEmail: string;
      buildingTypology: BuildingTypology;
      proposedUse: string;
      storeys: string;
      grossFloorArea: string;
      notes: string;
    },
    selectedFiles: SelectedFile[]
  ) => {
    setError(null);
    setInfo(null);
    setReviewText(null);
    setFindings([]);
    setSummary(null);
    setClassificationNotes(null);
    setMissingInformation([]);
    setHumanReviewSteps([]);
    setRequestId(null);
    setDecisions({});
    setAuditLog([]);
    setOverrideDrafts({});

    if (!applicationInput.address.trim()) {
      setError(t.permit.addressRequired);
      return;
    }

    try {
      setIsSubmitting(true);
      setInfo(t.permit.preparing);

      const documents: PermitDocumentPayload[] = [];
      for (const item of selectedFiles) {
        const textExcerpt = await readTextExcerpt(item.file);
        let storagePath: string | undefined;
        let signedUrl: string | undefined;

        if (user?.id) {
          const upload = await uploadPermitFileToUserBucket(
            user.id,
            item.file,
            item.file.name,
            item.file.type || undefined,
            { source: 'permitreview', category: item.category }
          );

          if (upload.success) {
            storagePath = upload.path;
            signedUrl = upload.url;
          } else {
            console.warn('Permit file upload skipped:', upload.error);
          }
        }

        documents.push({
          name: item.file.name,
          contentType: item.file.type || 'application/octet-stream',
          category: item.category,
          sizeBytes: item.file.size,
          storagePath,
          signedUrl,
          textExcerpt,
        });
      }

      setInfo(t.permit.running);

      const response = await axios.post(
        `${config.API_URL}/api/permit-review/analyze`,
        {
          application: {
            address: applicationInput.address.trim(),
            municipality: applicationInput.municipality.trim() || 'Toronto',
            applicantName: applicationInput.applicantName.trim() || undefined,
            contactEmail: applicationInput.contactEmail.trim() || undefined,
            buildingTypology: applicationInput.buildingTypology || undefined,
            proposedUse: applicationInput.proposedUse.trim() || undefined,
            storeys: applicationInput.storeys.trim() || undefined,
            grossFloorArea: applicationInput.grossFloorArea.trim() || undefined,
            notes: applicationInput.notes.trim() || undefined,
          },
          documents,
        },
        {
          timeout: 600000,
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Invalid response from permit review service');
      }

      const nextFindings: ComplianceFinding[] = Array.isArray(response.data.findings)
        ? response.data.findings
        : [];

      if (nextFindings.length === 0 && !response.data.review) {
        throw new Error('Review returned no structured findings');
      }

      setFindings(nextFindings);
      setSummary(response.data.summary || null);
      setClassificationNotes(response.data.classificationNotes || null);
      setMissingInformation(
        Array.isArray(response.data.missingInformation) ? response.data.missingInformation : []
      );
      setHumanReviewSteps(
        Array.isArray(response.data.humanReviewSteps) ? response.data.humanReviewSteps : []
      );
      setReviewText(response.data.review || null);
      setRequestId(response.data.requestId);
      setInfo(translate(t.permit.complete, { count: nextFindings.length }));
    } catch (err: any) {
      console.error('Permit review error:', err);
      const data = err?.response?.data;
      setError(data?.message || err.message || 'Permit package review failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    await runReview(
      {
        address,
        municipality,
        applicantName,
        contactEmail,
        buildingTypology,
        proposedUse,
        storeys,
        grossFloorArea,
        notes,
      },
      files
    );
  };

  const handleLoadSample = () => {
    applySamplePackage();
  };

  const handleLoadSampleAndRun = async () => {
    const { application, selectedFiles } = applySamplePackage();
    await runReview(application, selectedFiles);
  };

  const curlExample = `curl -X POST "${config.API_URL}/api/permit-review/analyze" \\
  -H "Content-Type: application/json" \\
  -d '{
    "application": {
      "address": "123 Demo Street, Toronto, ON",
      "municipality": "Toronto",
      "buildingTypology": "part9_house",
      "storeys": "2",
      "grossFloorArea": "240 m2"
    },
    "documents": [
      { "name": "floor-plan.pdf", "contentType": "application/pdf", "category": "drawing_pdf", "sizeBytes": 12000 }
    ]
  }'`;

  const copyCurl = async () => {
    try {
      await navigator.clipboard.writeText(curlExample);
      setApiCopied(true);
      setTimeout(() => setApiCopied(false), 2000);
    } catch (err) {
      console.warn('Clipboard copy failed', err);
    }
  };

  const appendAudit = (
    finding: ComplianceFinding,
    action: ReviewerAction,
    previousStatus: FindingStatus,
    newStatus: FindingStatus,
    note: string
  ) => {
    const entry: AuditEntry = {
      id: `audit_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      timestamp: new Date().toISOString(),
      findingId: finding.id,
      topic: finding.topic,
      action,
      previousStatus,
      newStatus,
      note: note.trim(),
      codeReference: finding.codeReference,
    };
    setAuditLog((prev) => [entry, ...prev]);
  };

  const applyDecision = (
    finding: ComplianceFinding,
    action: ReviewerAction,
    newStatus: FindingStatus,
    note: string
  ) => {
    const previousStatus = decisions[finding.id]?.effectiveStatus || finding.status;
    const reviewedAt = new Date().toISOString();
    setDecisions((prev) => ({
      ...prev,
      [finding.id]: {
        action,
        effectiveStatus: newStatus,
        note: note.trim(),
        reviewedAt,
      },
    }));
    appendAudit(finding, action, previousStatus, newStatus, note);
    setOverrideDrafts((prev) => ({
      ...prev,
      [finding.id]: { status: newStatus, note: '', open: false },
    }));
  };

  const handleAccept = (finding: ComplianceFinding) => {
    const status = finding.status;
    applyDecision(finding, 'accept', status, t.permit.acceptNote);
  };

  const handleEscalate = (finding: ComplianceFinding) => {
    applyDecision(finding, 'escalate', 'uncertain', t.permit.escalateNote);
  };

  const openOverride = (finding: ComplianceFinding) => {
    setOverrideDrafts((prev) => ({
      ...prev,
      [finding.id]: {
        status: prev[finding.id]?.status || finding.status,
        note: prev[finding.id]?.note || '',
        open: true,
      },
    }));
  };

  const submitOverride = (finding: ComplianceFinding) => {
    const draft = overrideDrafts[finding.id];
    if (!draft) return;
    if (!draft.note.trim()) {
      setError(translate(t.permit.overrideNoteRequired, { id: finding.id }));
      return;
    }
    setError(null);
    applyDecision(finding, 'override', draft.status, draft.note.trim());
  };

  const exportCsv = () => {
    if (effectiveFindings.length === 0) return;
    const headers = [
      'id',
      'system_status',
      'effective_status',
      'topic',
      'rationale',
      'code_reference',
      'source',
      'reviewer_action',
      'reviewer_note',
    ];
    const rows = effectiveFindings.map((f) => [
      f.id,
      f.status,
      f.effectiveStatus,
      f.topic,
      f.rationale,
      f.codeReference || '',
      f.source,
      f.decision?.action || '',
      f.decision?.note || '',
    ]);
    const escape = (value: string) => `"${String(value).replace(/"/g, '""')}"`;
    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => escape(String(cell))).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `permit-review-${requestId || 'export'}-${locale}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPdf = () => {
    window.print();
  };

  return (
    <div
      className="permit-review-page"
      style={{
        paddingTop: useMobileLayout
          ? 'calc(60px + var(--app-chrome-height, 0px))'
          : 'calc(80px + var(--app-chrome-height, 0px))',
      }}
    >
      {!useMobileLayout && <LogoNav />}
      <div className="permit-review-inner">
        <div className="permit-review-card">
          <h1 className="permit-review-title">{t.permit.title}</h1>
          <p className="permit-review-subtitle">{t.permit.subtitle}</p>
          <p className="permit-bilingual-note">{t.permit.bilingualNote}</p>

          <div className="permit-flow-strip">
            <h2>{t.permit.flowTitle}</h2>
            <ol className="permit-flow-steps">
              <li>
                <strong>{t.permit.flow1}</strong>
                <span>{t.permit.flow1Desc}</span>
              </li>
              <li>
                <strong>{t.permit.flow2}</strong>
                <span>{t.permit.flow2Desc}</span>
              </li>
              <li>
                <strong>{t.permit.flow3}</strong>
                <span>{t.permit.flow3Desc}</span>
              </li>
              <li>
                <strong>{t.permit.flow4}</strong>
                <span>{t.permit.flow4Desc}</span>
              </li>
              <li>
                <strong>{t.permit.flow5}</strong>
                <span>{t.permit.flow5Desc}</span>
              </li>
            </ol>
          </div>

          <div className="permit-sample-actions">
            <button
              type="button"
              className="permit-sample-btn"
              onClick={handleLoadSample}
              disabled={isSubmitting}
            >
              {t.permit.sampleLoad}
            </button>
            <button
              type="button"
              className="permit-sample-btn primary"
              onClick={handleLoadSampleAndRun}
              disabled={isSubmitting}
            >
              {isSubmitting ? t.permit.submitting : t.permit.sampleRun}
            </button>
          </div>

          <div className="permit-ruleset-panel">
            <h2>{t.permit.rulesetTitle}</h2>
            {ruleset ? (
              <>
                <p className="permit-ruleset-meta">
                  <strong>{ruleset.title}</strong>
                  {' · '}
                  {ruleset.id} v{ruleset.version}
                  {' · '}
                  {ruleset.ruleCount} {t.permit.rules}
                  {' · '}
                  {t.permit.execution}:{' '}
                  {ruleset.executionOrder === 'deterministic_first_then_ai'
                    ? t.permit.rulesetExecution
                    : ruleset.executionOrder || t.permit.rulesetExecution}
                </p>
                <div className="permit-ruleset-tags">
                  <span className="permit-ruleset-tag">
                    {ruleset.formats?.current || 'JSON'}
                  </span>
                  {(ruleset.formats?.plannedAdapters || []).slice(0, 3).map((adapter) => (
                    <span key={adapter} className="permit-ruleset-tag muted">
                      {t.permit.rulesetPhase2Prefix} {adapter}
                    </span>
                  ))}
                </div>
                <ul className="permit-ruleset-list">
                  {(ruleset.rules || []).map((rule) => (
                    <li key={rule.id}>
                      <code>{rule.id}</code>
                      <span>
                        {rule.topic}
                        {rule.codeReference ? ` — ${rule.codeReference}` : ''}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="permit-ruleset-note">
                  {ruleset.description} {t.permit.rulesetNoteSuffix}
                </p>
              </>
            ) : (
              <p className="permit-ruleset-meta">
                {rulesetError || t.permit.rulesetLoading}
              </p>
            )}
          </div>

          <div className="permit-typology-card">
            <h2>{t.permit.typologyCardTitle}</h2>
            <p>{typologyCardText}</p>
          </div>

          <div className="permit-api-panel">
            <h2>{t.permit.apiTitle}</h2>
            <p className="permit-ruleset-meta">{t.permit.apiHelp}</p>
            <ul className="permit-api-endpoints">
              <li>
                <code>{t.permit.apiAnalyze}</code>
              </li>
              <li>
                <code>{t.permit.apiRuleset}</code>
              </li>
            </ul>
            <pre className="permit-api-curl">{curlExample}</pre>
            <button type="button" className="permit-export-btn" onClick={copyCurl}>
              {apiCopied ? t.permit.apiCopied : t.permit.apiCopy}
            </button>
          </div>

          <form className="permit-review-form" onSubmit={handleSubmit}>
            <div className="permit-review-grid">
              <div className="permit-review-field full">
                <label htmlFor="permit-address">{t.permit.address} *</label>
                <input
                  id="permit-address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="e.g. 123 Main Street, Toronto"
                  required
                />
              </div>

              <div className="permit-review-field">
                <label htmlFor="permit-municipality">{t.permit.municipality}</label>
                <input
                  id="permit-municipality"
                  value={municipality}
                  onChange={(e) => setMunicipality(e.target.value)}
                  placeholder="Toronto"
                />
              </div>

              <div className="permit-review-field full">
                <label htmlFor="permit-typology">{t.permit.typology}</label>
                <select
                  id="permit-typology"
                  value={buildingTypology}
                  onChange={(e) => setBuildingTypology(e.target.value as BuildingTypology)}
                >
                  <option value="">{t.permit.typologySelect}</option>
                  <optgroup label={t.permit.typologyGroupPart9}>
                    <option value="part9_house">{t.permit.typologyPart9House}</option>
                    <option value="part9_townhouse">{t.permit.typologyPart9Townhouse}</option>
                    <option value="part9_secondary_suite">{t.permit.typologyPart9Suite}</option>
                    <option value="part9_small_building">{t.permit.typologyPart9Small}</option>
                  </optgroup>
                  <optgroup label={t.permit.typologyGroupPart3}>
                    <option value="part3_residential">{t.permit.typologyPart3Residential}</option>
                    <option value="part3_assembly_business">{t.permit.typologyPart3Assembly}</option>
                    <option value="part3_care">{t.permit.typologyPart3Care}</option>
                    <option value="part3_industrial">{t.permit.typologyPart3Industrial}</option>
                    <option value="part3">{t.permit.typologyPart3}</option>
                  </optgroup>
                  <option value="other">{t.permit.typologyOther}</option>
                </select>
                <p className="permit-field-hint">{t.permit.typologyHelp}</p>
              </div>

              <div className="permit-review-field">
                <label htmlFor="permit-applicant">{t.permit.applicant}</label>
                <input
                  id="permit-applicant"
                  value={applicantName}
                  onChange={(e) => setApplicantName(e.target.value)}
                />
              </div>

              <div className="permit-review-field">
                <label htmlFor="permit-email">{t.permit.email}</label>
                <input
                  id="permit-email"
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                />
              </div>

              <div className="permit-review-field">
                <label htmlFor="permit-storeys">{t.permit.storeys}</label>
                <input
                  id="permit-storeys"
                  value={storeys}
                  onChange={(e) => setStoreys(e.target.value)}
                  placeholder={t.permit.storeysPlaceholder}
                  inputMode="numeric"
                />
                <p className="permit-field-hint">{t.permit.storeysHint}</p>
              </div>

              <div className="permit-review-field">
                <label htmlFor="permit-gfa">{t.permit.gfa}</label>
                <div className="permit-input-with-unit">
                  <input
                    id="permit-gfa"
                    value={grossFloorArea}
                    onChange={(e) => setGrossFloorArea(e.target.value)}
                    placeholder={t.permit.gfaPlaceholder}
                    inputMode="decimal"
                  />
                  <span className="permit-unit" aria-hidden="true">
                    {t.permit.gfaUnit}
                  </span>
                </div>
                <p className="permit-field-hint">{t.permit.gfaHint}</p>
              </div>

              <div className="permit-review-field full">
                <label htmlFor="permit-use">{t.permit.proposedUse}</label>
                <input
                  id="permit-use"
                  value={proposedUse}
                  onChange={(e) => setProposedUse(e.target.value)}
                />
              </div>

              <div className="permit-review-field full">
                <label htmlFor="permit-notes">{t.permit.notes}</label>
                <textarea
                  id="permit-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
            </div>

            <div className="permit-review-field">
              <label>{t.permit.files}</label>
              <div
                className={`permit-dropzone${dragActive ? ' active' : ''}`}
                onClick={() => fileInputRef.current?.click()}
                onDragEnter={(e) => {
                  e.preventDefault();
                  setDragActive(true);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragActive(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  setDragActive(false);
                }}
                onDrop={onDrop}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click();
                }}
              >
                <p>{t.permit.dropzone}</p>
                <p className="hint">{translate(t.permit.dropzoneHint, { max: MAX_FILES })}</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.ifc,.ifczip,.dwg,.dxf,.rvt,.txt,.csv,application/pdf"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    if (e.target.files) addFiles(e.target.files);
                    e.target.value = '';
                  }}
                />
              </div>

              {files.length > 0 && (
                <ul className="permit-file-list">
                  {files.map((item) => (
                    <li key={item.id} className="permit-file-item">
                      <span>
                        {item.file.name}{' '}
                        <span style={{ color: '#94a3b8' }}>
                          ({item.category} · {(item.file.size / 1024).toFixed(0)} KB)
                        </span>
                      </span>
                      <button type="button" onClick={() => removeFile(item.id)}>
                        {t.permit.remove}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="permit-actions">
              <button className="permit-submit" type="submit" disabled={isSubmitting}>
                {isSubmitting ? t.permit.submitting : t.permit.submit}
              </button>
            </div>
          </form>

          {error && <div className="permit-alert error">{error}</div>}
          {info && !error && <div className="permit-alert info">{info}</div>}

          {(findings.length > 0 || reviewText) && (
            <div className="permit-result">
              <div className="permit-result-header">
                <h2>{t.permit.resultsTitle}</h2>
                <div className="permit-export-actions">
                  <button type="button" className="permit-export-btn" onClick={exportCsv}>
                    {t.permit.exportCsv}
                  </button>
                  <button type="button" className="permit-export-btn" onClick={exportPdf}>
                    {t.permit.exportPdf}
                  </button>
                </div>
              </div>
              {requestId && (
                <div className="permit-result-meta">
                  {t.permit.requestId}: {requestId} · {t.permit.statusModel}
                </div>
              )}

              {summary && <p className="permit-summary">{summary}</p>}

              {findings.length > 0 && (
                <div className="permit-status-counts">
                  <span className="permit-count-pill meets">
                    {t.permit.countMeets}: {reviewedStatusCounts.meets}
                  </span>
                  <span className="permit-count-pill does_not_meet">
                    {t.permit.countFail}: {reviewedStatusCounts.does_not_meet}
                  </span>
                  <span className="permit-count-pill information_not_available">
                    {t.permit.countIna}: {reviewedStatusCounts.information_not_available}
                  </span>
                  <span className="permit-count-pill uncertain">
                    {t.permit.countUncertain}: {reviewedStatusCounts.uncertain}
                  </span>
                  <span className="permit-count-pill hitl">
                    {t.permit.humanReviewed}: {reviewProgress.reviewed}/{reviewProgress.total}
                  </span>
                </div>
              )}

              {classificationNotes && (
                <>
                  <h3>{t.permit.classificationNotes}</h3>
                  <p className="permit-summary">{classificationNotes}</p>
                </>
              )}

              {findings.length > 0 && (
                <>
                  <h3>{t.permit.hitlTitle}</h3>
                  <p className="permit-summary">{t.permit.hitlHelp}</p>
                  <div className="permit-findings-wrap">
                    <table className="permit-findings-table permit-findings-table-hitl">
                      <thead>
                        <tr>
                          <th>{t.permit.colStatus}</th>
                          <th>{t.permit.colTopic}</th>
                          <th>{t.permit.colRationale}</th>
                          <th>{t.permit.colCode}</th>
                          <th>{t.permit.colSource}</th>
                          <th>{t.permit.colReviewer}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {effectiveFindings.map((f) => {
                          const decision = f.decision;
                          const draft = overrideDrafts[f.id];
                          const systemStatus = f.status;
                          return (
                            <tr
                              key={f.id}
                              className={decision ? `hitl-row hitl-${decision.action}` : undefined}
                            >
                              <td>
                                <span className={`permit-status-badge ${f.effectiveStatus}`}>
                                  {STATUS_LABELS[f.effectiveStatus] || f.effectiveStatus}
                                </span>
                                {decision && decision.effectiveStatus !== systemStatus && (
                                  <div className="permit-status-was">
                                    {t.permit.was}: {STATUS_LABELS[systemStatus]}
                                  </div>
                                )}
                              </td>
                              <td>
                                <strong>{f.topic}</strong>
                                <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 4 }}>
                                  {f.id}
                                </div>
                              </td>
                              <td>{f.rationale}</td>
                              <td>{f.codeReference || '—'}</td>
                              <td>
                                <span className={`permit-source-badge ${f.source}`}>
                                  {f.source === 'deterministic'
                                    ? t.permit.sourceDeterministic
                                    : t.permit.sourceAi}
                                </span>
                              </td>
                              <td>
                                {decision ? (
                                  <div className="permit-hitl-done">
                                    <span className={`permit-hitl-badge ${decision.action}`}>
                                      {ACTION_LABELS[decision.action]}
                                    </span>
                                    {decision.note && (
                                      <div className="permit-hitl-note">{decision.note}</div>
                                    )}
                                    <button
                                      type="button"
                                      className="permit-hitl-link"
                                      onClick={() => openOverride(f)}
                                    >
                                      {t.permit.change}
                                    </button>
                                  </div>
                                ) : (
                                  <div className="permit-hitl-actions">
                                    <button
                                      type="button"
                                      className="permit-hitl-btn accept"
                                      onClick={() => handleAccept(f)}
                                    >
                                      {t.permit.accept}
                                    </button>
                                    <button
                                      type="button"
                                      className="permit-hitl-btn override"
                                      onClick={() => openOverride(f)}
                                    >
                                      {t.permit.override}
                                    </button>
                                    <button
                                      type="button"
                                      className="permit-hitl-btn escalate"
                                      onClick={() => handleEscalate(f)}
                                    >
                                      {t.permit.escalate}
                                    </button>
                                  </div>
                                )}

                                {draft?.open && (
                                  <div className="permit-override-panel">
                                    <label>
                                      {t.permit.newStatus}
                                      <select
                                        value={draft.status}
                                        onChange={(e) =>
                                          setOverrideDrafts((prev) => ({
                                            ...prev,
                                            [f.id]: {
                                              ...prev[f.id],
                                              status: e.target.value as FindingStatus,
                                              open: true,
                                              note: prev[f.id]?.note || '',
                                            },
                                          }))
                                        }
                                      >
                                        {ALL_STATUSES.map((status) => (
                                          <option key={status} value={status}>
                                            {STATUS_LABELS[status]}
                                          </option>
                                        ))}
                                      </select>
                                    </label>
                                    <label>
                                      {t.permit.overrideNote}
                                      <textarea
                                        value={draft.note}
                                        onChange={(e) =>
                                          setOverrideDrafts((prev) => ({
                                            ...prev,
                                            [f.id]: {
                                              ...prev[f.id],
                                              status: prev[f.id]?.status || f.status,
                                              open: true,
                                              note: e.target.value,
                                            },
                                          }))
                                        }
                                        placeholder={t.permit.overridePlaceholder}
                                        rows={2}
                                      />
                                    </label>
                                    <div className="permit-override-actions">
                                      <button
                                        type="button"
                                        className="permit-hitl-btn override"
                                        onClick={() => submitOverride(f)}
                                      >
                                        {t.permit.saveOverride}
                                      </button>
                                      <button
                                        type="button"
                                        className="permit-hitl-link"
                                        onClick={() =>
                                          setOverrideDrafts((prev) => ({
                                            ...prev,
                                            [f.id]: {
                                              ...prev[f.id],
                                              status: prev[f.id]?.status || f.status,
                                              note: prev[f.id]?.note || '',
                                              open: false,
                                            },
                                          }))
                                        }
                                      >
                                        {t.permit.cancel}
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <h3>{t.permit.auditTitle}</h3>
                  {auditLog.length === 0 ? (
                    <p className="permit-summary">{t.permit.auditEmpty}</p>
                  ) : (
                    <div className="permit-audit-wrap">
                      <table className="permit-audit-table">
                        <thead>
                          <tr>
                            <th>{t.permit.auditTime}</th>
                            <th>{t.permit.auditFinding}</th>
                            <th>{t.permit.auditAction}</th>
                            <th>{t.permit.auditChange}</th>
                            <th>{t.permit.auditNote}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {auditLog.map((entry) => (
                            <tr key={entry.id}>
                              <td>{new Date(entry.timestamp).toLocaleString()}</td>
                              <td>
                                <strong>{entry.findingId}</strong>
                                <div className="permit-audit-topic">{entry.topic}</div>
                              </td>
                              <td>
                                <span className={`permit-hitl-badge ${entry.action}`}>
                                  {ACTION_LABELS[entry.action]}
                                </span>
                              </td>
                              <td>
                                {STATUS_LABELS[entry.previousStatus]} →{' '}
                                {STATUS_LABELS[entry.newStatus]}
                              </td>
                              <td>
                                {entry.note || '—'}
                                {entry.codeReference && (
                                  <div className="permit-audit-topic">{entry.codeReference}</div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}

              {missingInformation.length > 0 && (
                <>
                  <h3>{t.permit.missingInfo}</h3>
                  <ul className="permit-side-list">
                    {missingInformation.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </>
              )}

              {humanReviewSteps.length > 0 && (
                <>
                  <h3>{t.permit.humanSteps}</h3>
                  <ul className="permit-side-list">
                    {humanReviewSteps.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </>
              )}

              {reviewText && findings.length === 0 && (
                <div className="permit-result-body">{reviewText}</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PermitReview;
